import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Modal} from '@shared/components/common/modal/modal';
import {FileInputComponent} from '@shared/components/form/file-input/file-input.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import {LabelComponent} from '@shared/components/form/label/label.component';
// Descripción oculta en el template; volver a importar al descomentarla
// import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {AppFormComponent} from '@shared/components/ui/list-components';
import {CreationButtons} from '@shared/components/ui/creation-buttons/creation-buttons';
import {ErrorService} from '@shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {hoyLocal} from '@shared/utils/fecha-local';
import {PresupuestosClienteService} from '../presupuestos-cliente.service';
import {PresupuestoCliente} from '../presupuestos.interface';

interface DialogData {
  clienteId: number;
  presupuesto: PresupuestoCliente | null;
}

@Component({
  selector: 'app-crear-presupuesto',
  imports: [
    HasPermissionDirective,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    Modal,
    AppFormComponent,
    LabelComponent,
    InputFieldComponent,
    // TextareaFieldComponent,
    FileInputComponent,
    CreationButtons,
  ],
  templateUrl: './crear-presupuesto.html',
})
export class CrearPresupuesto implements OnInit {

  saveForm!: FormGroup;
  loading = false;
  isEditing = false;
  archivoActual: string | null = null;

  private fB = inject(FormBuilder);
  private presupuestosService = inject(PresupuestosClienteService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CrearPresupuesto>);
  private dialogData: DialogData = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.isEditing = !!this.dialogData.presupuesto;
    this.archivoActual = this.dialogData.presupuesto?.archivo_nombre ?? null;
    this.makeForm();
  }

  private makeForm(): void {
    const presupuesto = this.dialogData.presupuesto;

    // Todo opcional menos el cliente: el presupuesto se puede cargar por partes.
    this.saveForm = this.fB.group({
      cli_cliente_id: [this.dialogData.clienteId, Validators.required],
      // Al crear arranca en hoy; al editar se respeta lo guardado aunque esté vacío
      fecha: [presupuesto ? (presupuesto.fecha || '') : hoyLocal()],
      fecha_envio: [presupuesto?.fecha_envio || ''],
      fecha_aceptacion: [presupuesto?.fecha_aceptacion || ''],
      descripcion: [presupuesto?.descripcion || ''],
      presupuesto_nro: [presupuesto?.presupuesto_nro || ''],
      monto: [presupuesto?.monto ?? ''],
      archivo: [null as File | null],
    });
  }

  save(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = this.preparePayload();

    const peticion = this.isEditing
      ? this.presupuestosService.update(this.dialogData.presupuesto!.id, payload)
      : this.presupuestosService.store(payload);

    peticion.subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar(
            this.isEditing ? 'Presupuesto actualizado correctamente' : 'Presupuesto cargado correctamente'
          );
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private preparePayload(): FormData {
    const formValue = this.saveForm.value;
    const payload = new FormData();

    payload.append('cli_cliente_id', String(formValue.cli_cliente_id));
    payload.append('fecha', formValue.fecha || '');
    payload.append('presupuesto_nro', formValue.presupuesto_nro || '');
    payload.append('monto', formValue.monto === null || formValue.monto === '' ? '' : String(formValue.monto));
    payload.append('fecha_envio', formValue.fecha_envio || '');
    payload.append('fecha_aceptacion', formValue.fecha_aceptacion || '');
    payload.append('descripcion', formValue.descripcion || '');

    if (formValue.archivo) {
      payload.append('archivo', formValue.archivo, formValue.archivo.name);
    }

    if (this.isEditing) {
      payload.append('_method', 'PUT');
    }

    return payload;
  }

  cancel(): void {
    this.saveForm.reset();
    this.dialogRef.close();
  }
}
